import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, Trash2 } from 'lucide-react';
import { freeFetch, formatDate, RPE_LABELS, SESSION_TYPE_LABELS } from './api';
import { GripPill } from './GripPill';

const TYPE_LABELS = SESSION_TYPE_LABELS;

const gripsOf = (s) => [1, 2, 3].map((n) => s[`set${n}_grip`] || 'pullup');
const loggedSetNumbers = (s) => [1, 2, 3].filter((n) => Number(s[`set${n}_reps`]) > 0);

function Stat({ label, value, testid, accent = false }) {
  return (
    <div className="border border-mono-line bg-mono-card p-5" data-testid={testid}>
      <p className="text-mono-mute font-heading text-[11px] font-bold uppercase tracking-[0.2em] mb-1.5">{label}</p>
      <p className={`font-heading text-2xl font-black ${accent ? 'text-crimson' : 'text-white'}`}>{value}</p>
    </div>
  );
}

export default function FreeSessionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [grips, setGrips] = useState(null);
  const [savingGrips, setSavingGrips] = useState(false);
  const [gripError, setGripError] = useState('');
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    freeFetch(`/sessions/${id}`)
      .then((d) => { setSession(d.session); setGrips(gripsOf(d.session)); })
      .catch((err) => setError(err.message));
  }, [id]);

  const saveGrips = async () => {
    if (savingGrips) return;
    setSavingGrips(true);
    setGripError('');
    try {
      const d = await freeFetch(`/sessions/${id}/grips`, {
        method: 'PATCH',
        body: JSON.stringify({ set1_grip: grips[0], set2_grip: grips[1], set3_grip: grips[2] }),
      });
      setSession(d.session);
      setGrips(gripsOf(d.session));
    } catch (err) {
      setGripError(err.message);
    } finally {
      setSavingGrips(false);
    }
  };

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await freeFetch(`/sessions/${id}`, { method: 'DELETE' });
      navigate('/free/progress', { replace: true });
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  const backLink = (
    <Link
      to="/free/progress"
      className="inline-flex items-center gap-1.5 text-mono-mute hover:text-white font-heading text-xs font-bold uppercase tracking-[0.15em] transition-colors mb-8"
      data-testid="session-detail-back"
    >
      <ChevronLeft className="h-4 w-4" /> My Progress
    </Link>
  );

  if (error) {
    return (
      <div data-testid="session-detail-error-page">
        {backLink}
        <div className="border border-mono-line bg-mono-card p-10 text-center">
          <p className="text-mono-mute font-body" data-testid="session-detail-error">{error}</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return <p className="text-mono-mute font-body" data-testid="session-detail-loading">Loading...</p>;
  }

  const gripsDirty = gripsOf(session).some((g, i) => g !== grips[i]);

  const reps = [session.set1_reps, session.set2_reps, session.set3_reps]
    .filter((r) => Number(r) > 0).join(' / ') || '—';

  return (
    <div className="max-w-2xl" data-testid="session-detail-page">
      {backLink}

      <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-white leading-none mb-1" data-testid="session-detail-date">
        {formatDate(session.date)}
      </h1>
      <p className="text-mono-mute font-heading text-xs font-bold uppercase tracking-[0.2em] mb-2" data-testid="session-detail-type">
        {TYPE_LABELS[session.session_type] || session.session_type} · Pull-ups
      </p>
      <p className="text-mono-mute font-body text-sm mb-8" data-testid="session-detail-bodyweight">
        {session.bodyweight_kg != null
          ? `Bodyweight on the day: ${session.bodyweight_kg} kg`
          : 'Bodyweight on the day: not recorded'}
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <Stat label="Added weight" value={`${session.load_kg}kg`} testid="session-detail-load" accent />
        <Stat label="Reps (S1/S2/S3)" value={reps} testid="session-detail-reps" />
        <Stat label="Effort" value={RPE_LABELS[session.rpe] || session.rpe} testid="session-detail-rpe" />
        <Stat label="Feel" value={<span className="text-base font-body font-normal">{session.feel}</span>} testid="session-detail-feel" />
      </div>

      <div className="border border-mono-line bg-mono-card p-5 mb-6" data-testid="session-detail-grips">
        <p className="text-mono-mute font-heading text-[11px] font-bold uppercase tracking-[0.2em] mb-3">Grip</p>
        <div className="space-y-3">
          {loggedSetNumbers(session).map((n) => (
            <div key={n} className="flex items-center justify-between gap-3">
              <span className="text-white font-body text-sm">
                Set {n} · {session[`set${n}_reps`]} {Number(session[`set${n}_reps`]) === 1 ? 'rep' : 'reps'}
              </span>
              <GripPill
                grip={grips[n - 1]}
                onChange={(g) => setGrips((prev) => prev.map((p, i) => (i === n - 1 ? g : p)))}
                testid={`session-detail-set${n}-grip-pill`}
              />
            </div>
          ))}
        </div>
        {gripsDirty && (
          <div className="flex items-center gap-3 mt-4" data-testid="session-detail-grips-actions">
            <button
              onClick={saveGrips}
              disabled={savingGrips}
              className="bg-white hover:bg-[#E5E5E5] disabled:opacity-50 text-black font-heading text-sm font-bold uppercase tracking-wider rounded-none px-5 py-2.5 transition-colors"
              data-testid="session-detail-grips-save"
            >
              {savingGrips ? 'Saving...' : 'Save grip'}
            </button>
            <button
              onClick={() => { setGrips(gripsOf(session)); setGripError(''); }}
              disabled={savingGrips}
              className="text-mono-mute hover:text-white font-body text-sm underline transition-colors"
              data-testid="session-detail-grips-cancel"
            >
              Cancel
            </button>
          </div>
        )}
        {gripError && <p className="text-red-400 font-body text-xs mt-3" data-testid="session-detail-grips-error">{gripError}</p>}
      </div>

      {session.discomfort && (
        <div className="border border-mono-line bg-mono-card p-5 mb-6 ph-no-capture" data-testid="session-detail-discomfort">
          <p className="text-mono-mute font-heading text-[11px] font-bold uppercase tracking-[0.2em] mb-1.5">Discomfort</p>
          <p className="text-white font-body">{session.discomfort_note || 'Reported — no detail added.'}</p>
        </div>
      )}

      {session.notes ? (
        <div className="bg-mono-card border-l-4 border-crimson p-5 mb-10 ph-no-capture" data-testid="session-detail-notes">
          <p className="text-mono-mute font-heading text-[11px] font-bold uppercase tracking-[0.2em] mb-1.5">Notes</p>
          <p className="text-white font-body leading-relaxed">{session.notes}</p>
        </div>
      ) : (
        <div className="mb-10" />
      )}

      <div className="border-t border-mono-line pt-6">
        {!confirming ? (
          <button
            onClick={() => setConfirming(true)}
            className="inline-flex items-center gap-2 border border-mono-line text-mono-mute hover:border-crimson hover:text-crimson font-heading text-sm font-bold uppercase tracking-wider rounded-none px-5 py-2.5 transition-colors"
            data-testid="session-delete-btn"
          >
            <Trash2 className="h-4 w-4" /> Delete session
          </button>
        ) : (
          <div data-testid="session-delete-confirm">
            <p className="text-white font-body mb-4">
              Delete this session? This cannot be undone and your progress stats will be recalculated.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="bg-crimson hover:bg-crimson-bright disabled:opacity-50 text-white font-heading text-sm font-bold uppercase tracking-wider rounded-none px-6 py-2.5 transition-colors"
                data-testid="session-delete-confirm-btn"
              >
                {deleting ? 'Deleting...' : 'Yes, delete'}
              </button>
              <button
                onClick={() => setConfirming(false)}
                disabled={deleting}
                className="text-mono-mute hover:text-white font-body text-sm underline transition-colors"
                data-testid="session-delete-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
